import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * Gets the consultation ID from the root element's data attribute
 * Falls back to 'animal-welfare-2025' if not found
 */
function getConsultationId(): string {
  const rootElement = document.getElementById('consultation-wizard-root');
  const consultationId = rootElement?.getAttribute('data-consultation-id');
  
  if (consultationId) {
    console.log('[i18n] Using consultation ID from data attribute:', consultationId);
    return consultationId;
  }
  
  // Fallback for development/standalone testing
  const fallbackId = 'animal-welfare-2025';
  console.log('[i18n] No data-consultation-id found, using fallback:', fallbackId);
  return fallbackId;
}

/**
 * Gets the language from the data-language attribute on the root element
 * Returns undefined if not found or not supported
 */
function getDataLanguageAttribute(): string | undefined {
  const rootElement = document.getElementById('consultation-wizard-root');
  const language = rootElement?.getAttribute('data-language');
  
  if (language) {
    const supportedLngs = ['en', 'cs', 'fr', 'pl', 'it', 'nl', 'es'];
    // Normalize language code (e.g., 'en-US' -> 'en')
    const normalizedLang = language.split('-')[0].toLowerCase();
    
    if (supportedLngs.includes(normalizedLang)) {
      console.log('[i18n] Using language from data-language attribute:', normalizedLang);
      return normalizedLang;
    } else {
      console.log('[i18n] data-language attribute has unsupported language:', language);
    }
  }
  
  return undefined;
}

// Track missing translation keys for reporting
interface MissingKeyInfo {
  language: string;
  fallbackLanguage: string;
  namespace: string;
  key: string;
}

const missingKeys = new Map<string, MissingKeyInfo>();
const FALLBACK_LANGUAGE = 'en'; // Matches fallbackLng in i18next config

/**
 * Logs missing translation keys and tracks them for summary reporting
 * @param lngs - Array of language codes (e.g., ['pl', 'en'])
 * @param ns - Namespace where the key was requested
 * @param key - The missing translation key
 * @param _fallbackValue - The fallback value that was used (not used in logging)
 * @param _updateMissing - Whether to update missing keys (not used)
 * @param _options - Additional options (not used)
 */
function handleMissingKey(
  lngs: readonly string[],
  ns: string,
  key: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _fallbackValue: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _updateMissing: boolean,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _options: unknown
): void {
  const currentLanguage = lngs[0];
  const namespace = ns;
  
  // Only log if we're falling back to a different language
  if (currentLanguage !== FALLBACK_LANGUAGE) {
    const missingKey = `${namespace}:${key}`;
    
    // Track unique missing keys
    if (!missingKeys.has(missingKey)) {
      missingKeys.set(missingKey, {
        language: currentLanguage,
        fallbackLanguage: FALLBACK_LANGUAGE,
        namespace,
        key,
      });
      
      // Log immediately when a missing key is detected
      console.warn(
        `[i18n] Missing translation: ${currentLanguage} -> ${FALLBACK_LANGUAGE} | namespace: "${namespace}" | key: "${key}"`
      );
    }
  }
}

// Create and configure language detector with custom detector for data-language attribute
const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: 'dataLanguage',
  lookup: () => {
    return getDataLanguageAttribute();
  },
});

// Initialize i18next with HTTP backend for dynamic translations
i18next
  .use(languageDetector)
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    // Language settings
    fallbackLng: 'en',
    supportedLngs: ['en', 'cs', 'fr', 'pl', 'it', 'nl', 'es'],

    // Language detection configuration
    detection: {
      order: ['querystring', 'dataLanguage', 'navigator'],
      lookupQuerystring: 'lang',
      caches: ['localStorage'],
    },

    // Backend configuration for loading translations from CDN
    backend: {
      loadPath: (lngs: string[], namespaces: string[]) => {
        const language = lngs[0];
        const namespace = namespaces[0];
        // Remove trailing slash from spacesUrl if present
        const spacesUrl = import.meta.env.VITE_SPACES_URL?.replace(/\/$/, '');
        const baseUrl = import.meta.env.BASE_URL || '/';

        // UI namespaces (signup, welcome, completion, etc.)
        if (namespace !== 'translation') {
          if (spacesUrl) {
            // Production/Dev Deploy: Load UI translations from CDN
            // BASE_URL will be '/app/' for production, '/app-dev/' for dev deploy
            // Ensure baseUrl starts with / and doesn't end with /
            const normalizedBaseUrl = baseUrl.startsWith('/') ? baseUrl : `/${baseUrl}`;
            const uiPath = `${spacesUrl}${normalizedBaseUrl}locales/${language}/${namespace}.json`;
            console.log(`[i18n] Loading UI translation: ${uiPath}`, {
              language,
              namespace,
              spacesUrl,
              baseUrl,
              normalizedBaseUrl,
              fullPath: uiPath
            });
            return uiPath;
          } else {
            // Local Development: Load from local public folder
            const localPath = `${baseUrl}locales/${language}/${namespace}.json`;
            console.log(`[i18n] Loading UI translation (local): ${localPath}`);
            return localPath;
          }
        }

        // Main consultation translations loaded from consultations directory or CDN
        // Get consultation ID from DOM element's data attribute
        const consultationId = getConsultationId();

        if (spacesUrl) {
          // Production/Dev Deploy: Load from DigitalOcean Spaces CDN
          const consultationPath = `${spacesUrl}/consultations/${consultationId}/translations/${language}.json`;
          console.log(`[i18n] Loading consultation translation: ${consultationPath}`);
          return consultationPath;
        } else {
          // Local Development: Load from consultations directory (served by Vite middleware)
          // Path structure matches production: consultations/{consultationId}/translations/{language}.json
          const localConsultationPath = `/consultations/${consultationId}/translations/${language}.json`;
          console.log(`[i18n] Loading consultation translation (local): ${localConsultationPath}`);
          return localConsultationPath;
        }
      },

      // Request options
      requestOptions: {
        mode: 'cors',
        credentials: 'same-origin',
        cache: 'default',
      },
    },

    // React-specific options
    react: {
      useSuspense: false, // Don't suspend rendering while loading translations
    },

    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Namespace settings
    defaultNS: 'translation',
    ns: ['translation', 'signup', 'welcome', 'howToUse', 'completion', 'application', 'common', 'guidance', 'app'],

    // Missing key detection
    saveMissing: true, // Enable missing key detection
    returnEmptyString: false, // Treat empty strings as missing translations
    missingKeyHandler: handleMissingKey,

    // Debug mode - temporarily enabled for troubleshooting
    debug: true, // import.meta.env.DEV,

    // Load translations on initialization
    load: 'languageOnly', // Load 'en' not 'en-US'
  })
  .then(() => {
    // Log summary of missing keys after initialization
    if (missingKeys.size > 0) {
      console.group('[i18n] Missing Translation Summary');
      console.log(`Total missing keys: ${missingKeys.size}`);
      
      // Group by namespace
      const byNamespace = new Map<string, string[]>();
      missingKeys.forEach((info) => {
        if (!byNamespace.has(info.namespace)) {
          byNamespace.set(info.namespace, []);
        }
        byNamespace.get(info.namespace)!.push(info.key);
      });
      
      byNamespace.forEach((keys, namespace) => {
        console.log(`\n${namespace}: ${keys.length} missing key(s)`);
        keys.forEach((key) => {
          console.log(`  - ${key}`);
        });
      });
      
      console.groupEnd();
    } else {
      console.log('[i18n] All translations loaded successfully - no missing keys detected');
    }
  });

// Function to change language and update document direction
export const changeLanguage = async (language: string) => {
  await i18next.changeLanguage(language);

  // Update document direction for RTL languages
  const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
  const isRTL = rtlLanguages.includes(language);
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
  document.documentElement.lang = language;
};

// Helper to detect if a language is RTL
export const isRTLLanguage = (language: string): boolean => {
  const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
  return rtlLanguages.includes(language);
};

// Export the configured instance
export default i18next;
